import { CommonModule } from '@angular/common';
import { Component, OnInit, input, model, effect, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectorModel } from 'src/app/fragments/selector/selector-model';
import { SelectorComponent } from 'src/app/fragments/selector/selector.component';
import { FilterModel } from '../../model/filter-model';
import { QueryElementsConfig } from '../../model/query-elements-config';
import { FilterDataService } from 'src/app/services/filter-data.service';

@Component({
  selector: 'app-video-query-elements',
  templateUrl: 'video-query-elements.component.html',
  styleUrls: ['video-query-elements.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, SelectorComponent],
})
export class VideoQueryElementsComponent implements OnInit {

  readonly accentColorInHex = input<string>('#007bff');
  readonly allowEmptyValues = input<boolean>(true);

  readonly queryElementsConfig = model<QueryElementsConfig>({
    gender: { visible: true, isOpen: false },
    category: { visible: true, isOpen: false },
    competition: { visible: true, isOpen: false },
    dateStart: { visible: true, isOpen: false },
    dateEnd: { visible: true, isOpen: false },
  });

  readonly filters = model.required<FilterModel>();

  selectedGender?: SelectorModel;
  genderOptions: SelectorModel[] = [];

  selectedCategory?: SelectorModel;
  categoryOptions: SelectorModel[] = [];

  selectedCompetition?: SelectorModel;
  competitionOptions: SelectorModel[] = [];

  dateStart: string = '';
  dateEnd: string = '';

  private isInternalUpdate = false;

  constructor(private filterDataService: FilterDataService) {
    effect(() => {
      const currentFilters = this.filters();
      if (!this.isInternalUpdate && this.genderOptions.length > 0) {
        untracked(() => this.syncSelectsFromFilters(currentFilters));
      }
    });
  }

  ngOnInit() {
    this.loadData();
  }

  protected loadData() {
    this.filterDataService.getVideoFilters().subscribe(data => {
      if (!data) return;

      this.genderOptions = data.genders.map(g => ({ id: g.id.toString(), label: g.name, value: g.name.toUpperCase() }));
      this.categoryOptions = data.categories.map(c => ({ id: c.id.toString(), label: c.name, value: c.id.toString() }));
      this.competitionOptions = data.competitions.map(c => ({ id: c.id.toString(), label: c.name, value: c.id.toString() }));

      if (this.allowEmptyValues()) {
        this.genderOptions.unshift({ id: '', label: 'Selecciona una opción', value: '' });
        this.categoryOptions.unshift({ id: '', label: 'Selecciona una opción', value: '' });
        this.competitionOptions.unshift({ id: '', label: 'Selecciona una opción', value: '' });
      }

      this.syncSelectsFromFilters(this.filters());
    });
  }

  protected syncSelectsFromFilters(f: FilterModel | undefined | null) {
    if (f) {
      this.selectedGender = this.getOption(f.gender, this.genderOptions) || this.genderOptions[0];
      this.selectedCategory = this.getOption(f.category, this.categoryOptions) || this.categoryOptions[0];
      this.selectedCompetition = this.getOption(f.competition, this.competitionOptions) || this.competitionOptions[0];
      this.dateStart = f.dateStart || '';
      this.dateEnd = f.dateEnd || '';
    } else {
      this.selectedGender = this.genderOptions ? this.genderOptions[0] : undefined;
      this.selectedCategory = this.categoryOptions ? this.categoryOptions[0] : undefined;
      this.selectedCompetition = this.competitionOptions ? this.competitionOptions[0] : undefined;
      this.dateStart = '';
      this.dateEnd = '';
    }
  }

  protected updateFilters() {
    this.isInternalUpdate = true;

    this.filters.set({
      gender: this.selectedGender?.value || this.genderOptions[0]?.value || '',
      category: this.selectedCategory?.value || this.categoryOptions[0]?.value || '',
      competition: this.selectedCompetition?.value || this.competitionOptions[0]?.value || '',
      dateStart: this.dateStart || '',
      dateEnd: this.dateEnd || ''
    });

    setTimeout(() => this.isInternalUpdate = false, 0);
  }

  protected getOption(value: string, options: SelectorModel[]): SelectorModel | undefined {
    return options.find(option => option.value === value);
  }

  protected onQueryElementsOpen(key: string, value: boolean) {
    const config = { ...this.queryElementsConfig() };

    // Convert key string to typed property
    const typedKey = key as keyof QueryElementsConfig;

    for (const k in config) {
      if (config.hasOwnProperty(k) && k !== key) {
        const otherKey = k as keyof QueryElementsConfig;
        if (config[otherKey]) {
          config[otherKey]!.isOpen = false;
        }
      }
    }

    if (config[typedKey]) {
      config[typedKey]!.isOpen = value;
    }

    this.queryElementsConfig.set(config);
  }
}
